import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  ApplicationStatus,
  RetryIdentityVerificationStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Applicant, Application } from "types/application";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { logger } from "logger";
import { handleCopy } from "utils/handleCopy";
import templatesApi from "api/landlord/templates";
import { useAuthContext } from "providers/Auth";
import { TemplateVerificationsSettings } from "types/landlord/verificationTemplate";
import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { IdentityVerificationRerunModal } from "shared/app/IdentityVerificationReport/components/IdentityVerificationRerunModal";
import { ReportVersionTabs } from "shared/app/IdentityVerificationReport/components/ReportVersionTabs";
import { IdentityVerificationRerunProgressView } from "./IdentityVerificationRerunProgressView";

const IdentityVerificationReportSlice = lazy(() => import("../index"));

const cancelRerunErrorMessageMap: Record<string, string> = {
  already_completed:
    "Unable to cancel because the applicant has already completed their identity verification"
};

interface IdentityVerificationReportSliceWrapperProps {
  applicant: Applicant;
  application: Application;
  defaultVerificationId: number;
  requiresOverridesIdentityVerification: boolean;
  templateTemplateSettings?: TemplateVerificationsSettings;
  /** The applicant has an outstanding identity verification request. */
  isVerificationInProgress: boolean;
}

export const IdentityVerificationReportSliceWrapper = ({
  applicant,
  application,
  defaultVerificationId,
  requiresOverridesIdentityVerification,
  templateTemplateSettings,
  isVerificationInProgress
}: IdentityVerificationReportSliceWrapperProps) => {
  const queryClient = useQueryClient();
  const { permissions } = useAuthContext();
  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<number>(
    defaultVerificationId
  );

  const reportVersions = applicant.identity_verification_verifications;
  const activeVersion = reportVersions?.find((report) => !report.archived_at);

  // Requesting a re-run makes a new report active and canceling one discards it
  // entirely, so the tab the partner was on can end up archived or gone. Either way the
  // report the server considers active is the one to show.
  useEffect(() => {
    setSelectedVersionId(defaultVerificationId);
  }, [defaultVerificationId]);

  const resolvedVersionId = reportVersions?.some(
    (report) => report.id === selectedVersionId
  )
    ? selectedVersionId
    : defaultVerificationId;
  const isViewingActiveVersion =
    !activeVersion || activeVersion.id === resolvedVersionId;

  const refetchApplication = useCallback(() => {
    queryClient
      .refetchQueries({ queryKey: ["APPLICATION", String(application.id)] })
      .catch((error) => {
        logger.error("Failed to invalidate queries", error);
      });
  }, [application.id, queryClient]);

  const { data: identityVerificationSchema } = useQuery({
    queryKey: ["VERIFICATION_SCHEMAS"],
    queryFn: () => templatesApi.getVerificationSchemas(),
    select: (data) =>
      data.verification_schemas.find(
        (schema) => schema.name === VerificationNames.IdentityVerification
      )
  });

  const rerunFee = identityVerificationSchema?.screening_fee;

  const { mutate: copyVerificationLink, isPending: isCopyingLink } =
    useMutation({
      mutationFn: () =>
        instance.post<string>(
          `/portal/v1/applications/${application.id}/applicants/${applicant.id}/identity_verification_link`
        ),
      onSuccess: ({ data }) => {
        handleCopy(data, "The link has been copied to your clipboard");
      },
      onError: (error) => {
        toast.error(
          `Request identity verification link failed: ${getErrorMessage(error)}`
        );
      }
    });

  const { mutate: cancelRerun, isPending: isCanceling } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/applicants/${applicant.id}/cancel_rerun_identity_verification`
      ),
    onSuccess: () => {
      refetchApplication();
      toast.success("Identity verification request successfully canceled");
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);

      // Either way the request is no longer outstanding, so the report list on screen is
      // the stale part. `not_a_rerun` means there is nothing left to cancel.
      if (["not_a_rerun", "already_completed"].includes(errorMessage)) {
        refetchApplication();

        if (errorMessage === "not_a_rerun") {
          toast.success("Identity verification request successfully canceled");
          return;
        }
      }

      toast.error(
        cancelRerunErrorMessageMap[errorMessage] ||
          `Cancel identity verification failed: ${errorMessage}`
      );
    }
  });

  const disabledRerunTooltip = useMemo(() => {
    if (!permissions.canEditIdv.rerunIdvReport)
      return "You don't have permission to re-run the identity verification report";
    if (!isViewingActiveVersion)
      return "Please select the latest identity verification report to re-run";
    if (isVerificationInProgress)
      return "An identity verification request is already in progress";
    if (
      activeVersion &&
      [
        VerificationStatus.Pending,
        VerificationStatus.Started,
        VerificationStatus.Processing
      ].includes(activeVersion.status)
    )
      return "Cannot re-run identity verification until the active report finishes";

    return undefined;
  }, [
    permissions.canEditIdv.rerunIdvReport,
    isViewingActiveVersion,
    isVerificationInProgress,
    activeVersion
  ]);

  const onRerunReport = () => setRerunModalOpen(true);

  const rerunModal = (
    <IdentityVerificationRerunModal
      opened={rerunModalOpen}
      onClose={() => setRerunModalOpen(false)}
      applicantId={applicant.id}
      applicationId={application.id}
      rerunFee={rerunFee}
      refetchIdv={refetchApplication}
    />
  );

  // While a request is outstanding there is no report to show for the active
  // version, so the partner gets the request status plus a way to resend or undo it.
  if (isVerificationInProgress && isViewingActiveVersion)
    return (
      <div className="bg-white">
        <ReportHeader
          title="Identity verification"
          name={VerificationNames.IdentityVerification}
          applicationStatus={application.status}
          applicationId={application.id}
          applicantId={applicant.id}
          onRerunReport={onRerunReport}
          disabledRerunModalTooltip={disabledRerunTooltip}
          hidePrint
        />
        <ReportVersionTabs
          versions={reportVersions}
          selectedVersionId={resolvedVersionId}
          onSelectVersion={setSelectedVersionId}
        />
        <IdentityVerificationRerunProgressView
          requestedAt={activeVersion?.created_at}
          actions={
            <>
              <Button
                size={ButtonSize.BASE}
                variant={ButtonVariant.BASE}
                onClick={() => copyVerificationLink()}
                loading={isCopyingLink}
                disabled={isCopyingLink}
              >
                Copy link
              </Button>
              <Button
                size={ButtonSize.BASE}
                variant={ButtonVariant.PRIMARY}
                onClick={() => cancelRerun()}
                loading={isCanceling}
                disabled={isCanceling}
              >
                Cancel verification
              </Button>
            </>
          }
        />
        {rerunModal}
      </div>
    );

  return (
    <>
      <Suspense fallback={null}>
        <IdentityVerificationReportSlice
          applicationStatus={application.status as ApplicationStatus}
          requiresOverridesIdentityVerification={
            requiresOverridesIdentityVerification
          }
          retryIdentityVerificationStatus={
            applicant.retry_identity_verification_status as
              | RetryIdentityVerificationStatus
              | undefined
          }
          applicationId={String(application.id)}
          verificationId={resolvedVersionId}
          applicant={applicant}
          templateTemplateSettings={templateTemplateSettings}
          onRerunReport={onRerunReport}
          disabledRerunTooltip={disabledRerunTooltip}
          reportVersions={reportVersions}
          selectedVersionId={resolvedVersionId}
          onSelectVersion={setSelectedVersionId}
        />
      </Suspense>
      {rerunModal}
    </>
  );
};
